import { cn } from '@invent/wl-ui-kit-next';
import { Button, Card, CardContent } from '@invent/wl-ui-kit-next/ui';
import { LoaderCircle, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';

import type {
  IAnswer,
  IFinding,
  IRenderState,
  IRepeatRow,
  IResponseState,
  ISnapshotUsage
} from '../api/contracts';
import { CONTAINER_FIELD_TYPES, decodeAnswer, isCellsField, type UiValue } from './answer-values';
import {
  isConfirmOnly,
  lockedCells,
  PERSON_REGISTRIES,
  prefillLock,
  prefillView,
  proposedCellsText,
  sourceCopy
} from './prefill-view';
import { LinkedText } from './linked-text';
import { LookupAside } from './lookup-note';
import { MEMBER_LOOKUPS, RESPONDENT_LOOKUPS } from './lookup-shapes';
import { OfficeAddressBlock, proposedAddresses } from './office-address';
import {
  ResponseField,
  type ILockedCells,
  type IOptionFollowUps,
  type IRowPrefill
} from './response-field';
import { QuestionLabel } from './question-label';
import { rangeHighOf, repeatRowHead } from './row-heads';
import { tableLayout } from './table-layout';
import { WarningLine } from './warning-line';
import {
  activeRows,
  addressKey,
  answerInstanceKey,
  domAddress,
  findingText,
  gridRows,
  isChildTriggered,
  isUsageRequired,
  isUsageVisible,
  multiYearRows,
  selectedCodes,
  type ISectionView,
  type IUsageNode
} from './snapshot-view';
import { RESPONDENT_LOOKUP, type IAnswerAddress, type ILookupNote } from './use-response';

interface ISectionProps {
  view: ISectionView;
  state: IResponseState;
  /** What to draw: `IUseResponse.render`, survey-api's render with the unsaved answers' moves. */
  render: IRenderState;
  /** `y0`/`y-1` → label, from the cycle config, for multi-year tables. */
  yearLabels: Record<string, string> | undefined;
  drafts: Map<string, UiValue>;
  /** Addresses whose typed text no payload can carry — see `IUseResponse.malformed`. */
  malformed: ReadonlySet<string>;
  answers: Map<string, IAnswer>;
  findings: Map<string, IFinding[]>;
  /** Addresses a rejection emptied in this session — see `IUseResponse.clearedPrefill`. */
  clearedPrefill: ReadonlySet<string>;
  readOnly: boolean;
  onChange: (address: IAnswerAddress, value: UiValue, usage: ISnapshotUsage) => void;
  /** A typed field was left — see `IUseResponse.commit`. */
  onCommit: () => void;
  onConfirm: (address: IAnswerAddress, action: 'confirm' | 'reject') => void;
  /** One decision about several proposals in one batch — an office address's lines (FA5). */
  onDecide?: (addresses: IAnswerAddress[], action: 'confirm' | 'reject') => void;
  /** "No, not this person" on a member row — `IUseResponse.unlinkMember`. */
  onUnlink?: (instanceId: string, names: IAnswerAddress[]) => void;
  /** This session's CRD lookups (`IUseResponse.lookups`); absent where nothing is looked up. */
  lookups?: ReadonlyMap<string, ILookupNote>;
  /** Settles once the row is made, or refused: "Add" waits for it. */
  onAddRow: (usageCode: string) => void | Promise<void>;
  onRemoveRow: (instanceId: string) => void;
}

/**
 * Renders one section: its root usages, their children, and their repeat rows.
 *
 * One question per surface (the two-portals design, §3.3): each root, with everything that hangs
 * off it, on a white card of its own over the page's off-white ground, so a question and the
 * sub-questions it opens read as one thing and the next question as the next. A range's two ends
 * share a card, side by side from `sm` (`rangeHighOf`); an office address a registry proposed
 * whole is one card, its lines drawn as one address to decide (`proposedAddresses`).
 */
export function ResponseSectionView(props: ISectionProps) {
  const { view, state, render, onDecide } = props;
  const offices =
    onDecide && !props.readOnly
      ? proposedAddresses(props.answers, props.drafts, (code) => {
          const usage = usageInSection(view, code);
          return usage && isUsageVisible(code, render) ? usage : undefined;
        })
      : [];
  const cards: IUsageNode[][] = [];
  for (const node of view.roots) {
    if (!isUsageVisible(node.usage.code, render)) continue;
    const office = offices.find((address) => address.codes.includes(node.usage.code));
    if (office) {
      if (office.codes[0] === node.usage.code) cards.push([node]);
      continue;
    }
    const last = cards.at(-1);
    if (last?.length === 1 && rangeHighOf(last[0].usage.code) === node.usage.code) last.push(node);
    else cards.push([node]);
  }

  return (
    <div className="grid gap-4">
      {cards.map((card) => {
        const office = offices.find((address) => address.codes[0] === card[0].usage.code);
        return (
          <div
            key={card[0].usage.code}
            className={cn(
              'min-w-0 rounded-[0.625rem] border border-border bg-card px-3 py-4 sm:px-5 sm:py-5',
              card.length === 2 && 'grid items-start gap-4 sm:grid-cols-2'
            )}
          >
            {office && onDecide ? (
              <OfficeAddressBlock
                address={office}
                findings={props.findings}
                usageOf={(code) => usageInSection(view, code)}
                onDecide={onDecide}
              />
            ) : (
              card.map((node) => (
                <UsageNodeView key={node.usage.code} node={node} instanceId={null} {...props} />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}

interface INodeProps extends ISectionProps {
  node: IUsageNode;
  /** The repeat row this subtree is being rendered inside, if any. */
  instanceId: Nullable<string>;
  /** A percent group's member: a percent field is drawn on one line (`ResponseField.compact`). */
  compact?: boolean;
  /** The one box an option opened, drawn on the option's line (`ResponseField.beside`). */
  beside?: boolean;
}

function UsageNodeView(props: INodeProps) {
  const { node, instanceId, findings, compact = false, beside = false } = props;
  const { usage } = node;

  if (usage.fieldType === 'repeat_group') {
    return <RepeatGroupView {...props} />;
  }

  // A container's own finding is addressed at the scalar key, which is also what
  // `answerInstanceKey` answers for one — see its doc for why the two agree here and
  // deliberately do not for a scalar `per_option_sub`. Left as a literal `''` rather than
  // routed through it, on that function's own instruction.
  const ownFindings = findings.get(addressKey(usage.code, instanceId, '')) ?? [];
  // A breakout's sub-block (GA §6): "Of which" and its total on one line, the parts under them.
  const subBlock = usage.params?.percent_group?.sum_rule === 'eq_parent';
  // The running total and the compact grid are a percent group's (D16); a display note's
  // children are questions like any others, whatever their type.
  const percentGroup = usage.fieldType === 'percent_group';
  const total = percentGroup && (
    <p className="text-sm font-medium" data-percent-total>
      Total: {percentTotal(props)}%
    </p>
  );

  return (
    <div className={cn('grid min-w-0', compact ? 'gap-2' : 'gap-4')}>
      {CONTAINER_FIELD_TYPES.has(usage.fieldType) ? (
        // A percent group carries no answer of its own; its children do, and the
        // engine's own `engine.percent_group` finding is what reports the sum —
        // addressed to the group, so it is rendered here. Nothing else would:
        // there is no control at this address for `ResponseField` to hang it off.
        <fieldset
          className={cn(
            groupClasses(ownFindings),
            'gap-2',
            subBlock && 'grid-cols-[minmax(0,1fr)_auto] items-baseline'
          )}
          // A container is a rendered field and carries its address like any other, so a
          // spec — and the group's own `engine.percent_group` finding below — can be
          // scoped to it. `ResponseField` stamps this for leaf fields and is never reached
          // for a container, which is why it has to be stamped here too.
          //
          // The third segment is a literal `''`, and `answerInstanceKey(usage)` returns `''`
          // here too (a `percent_group` is in `CONTAINER_FIELD_TYPES`) — the same string
          // either way, written as the literal so the file states one rule about containers
          // rather than two. What must not change is the value: a container holds no answer
          // of its own, and `SyntheticChecks` emits its `engine.percent_group` finding at a
          // hardcoded blank instanceKey even when the container is itself option-triggered
          // (July's `RIA-S9-001/growth_sources`), so this stays blank rather than following a
          // scalar `per_option_sub` to its trigger code. survey-api's own snapshot
          // `answerInstanceKey` reports the trigger code for that usage and is the thing
          // *not* to adopt here — `answerInstanceKey`'s doc (`snapshot-view.ts`) and
          // `e2e/specs/ria/container-address.spec.ts` both hold this in place.
          data-address={domAddress(usage.code, instanceId, '')}
        >
          {/* A breakout's sub-block sits under its category and carries its label again (GA §6);
              said once is enough. Floated, a legend is laid out as any other box, so the
              sub-block's total can share its line; it still names the group. */}
          <QuestionLabel
            as="legend"
            className={cn(
              'text-sm font-medium text-[var(--color-royal-blue)]',
              subBlock && 'float-left'
            )}
            text={subBlock ? 'Of which' : usage.label}
            required={isUsageRequired(usage.code, props.render, instanceId)}
          />
          {subBlock && total}
          {usage.helperText && (
            <p className="col-span-full text-sm text-muted-foreground">
              <LinkedText text={usage.helperText} />
            </p>
          )}
          <GroupFindings
            findings={ownFindings}
            usageOf={(code) => usageInSection(props.view, code)}
            className="col-span-full"
          />
          <ChildFields {...props} asGroup={percentGroup} />
          {!subBlock && total}
        </fieldset>
      ) : (
        <>
          <FieldFor
            {...props}
            usage={usage}
            instanceId={instanceId}
            compact={compact && usage.fieldType === 'percent'}
            beside={beside}
            optionFollowUps={optionFollowUps(props, node, instanceId, (child, oneBox) => (
              <UsageNodeView
                key={child.usage.code}
                {...props}
                node={child}
                compact={false}
                beside={oneBox}
              />
            ))}
          />
          <ChildFields {...props} />
        </>
      )}
    </div>
  );
}

/**
 * The same left rule `ResponseField` puts on a flagged question, for a group that
 * carries a finding of its own. Always laid out, transparent until it is needed,
 * so a finding appearing does not move the section under the respondent.
 */
function groupClasses(findings: IFinding[]): string {
  return cn(
    'grid gap-4 rounded-r-md border-l-2 border-transparent px-3 py-2 transition-colors',
    findings.some((finding) => finding.severity === 'hard') && 'border-destructive bg-destructive/5'
  );
}

/**
 * Findings addressed to a group rather than to a control — a percent group's sum
 * rule, a repeat group's row count. Without this they were indexed and then
 * dropped on the floor: the stepper flagged the section and nothing on the page
 * said what to fix.
 */
function GroupFindings({
  findings,
  usageOf,
  className
}: {
  findings: IFinding[];
  /** The section's usages, for a finding said by the questions' labels (`findingText`). */
  usageOf?: (code: string) => ISnapshotUsage | undefined;
  className?: string;
}) {
  if (findings.length === 0) return null;

  return (
    <div className={cn('grid gap-1', className)}>
      {findings.map((finding) => {
        const key = `${finding.code}-${finding.ruleId ?? ''}-${finding.severity}`;
        const words = findingText(finding, usageOf, 'inline');
        return finding.severity === 'hard' ? (
          <p key={key} className="text-sm font-medium text-destructive">
            {words}
          </p>
        ) : (
          <WarningLine key={key}>{words}</WarningLine>
        );
      })}
    </div>
  );
}

/**
 * Sub-questions and per-option subs, indented under the question they hang off — those a list
 * draws under their option (`underOption`) excepted.
 *
 * A percent group's members (`asGroup`) sit compact (the survey review's R23, R28): a percent
 * field on one line, three to a row from `lg`; a member with a sub-block of its own, or any other
 * kind, takes a row of its own. Members worded at length (RIA-OS-001's advisor models) take a
 * row each: three to a row, a paragraph is cut into a column a few words wide.
 */
function ChildFields({ asGroup = false, ...props }: INodeProps & { asGroup?: boolean }) {
  const { node, instanceId } = props;
  const visible = afterList(node, visibleChildren(props, node, instanceId));

  if (visible.length === 0) return null;
  const worded = visible.some((child) => child.usage.label.length > SHORT_MEMBER_LABEL);

  return (
    <div
      className={cn(
        'grid gap-4 border-l-2 border-[var(--color-slight-blue)] pl-4',
        asGroup && 'col-span-full gap-x-4 gap-y-1',
        asGroup && !worded && 'sm:grid-cols-2 lg:grid-cols-3'
      )}
    >
      {visible.map((child) => {
        const view = (
          <UsageNodeView key={child.usage.code} {...props} node={child} compact={asGroup} />
        );
        const wide = child.usage.fieldType !== 'percent' || child.children.length > 0;
        return asGroup && wide ? (
          <div key={child.usage.code} className="col-span-full">
            {view}
          </div>
        ) : (
          view
        );
      })}
    </div>
  );
}

/** The longest member label a percent group still lays three to a row: its own longest is 31. */
const SHORT_MEMBER_LABEL = 40;

/**
 * "Total: n%" under a percent group (the survey review's R9, R23; the plan's D16): the sum of its
 * percent members as they stand on screen, the typing included, a sub-block's parts not counted
 * twice. Arithmetic, never a verdict — whether the sum is right is the engine's
 * `engine.percent_group` finding.
 */
function percentTotal(props: INodeProps): string {
  const { node, instanceId, drafts, answers } = props;
  let total = 0;
  for (const child of visibleChildren(props, node, instanceId)) {
    const { usage } = child;
    if (usage.fieldType !== 'percent') continue;
    const key = addressKey(usage.code, instanceId, answerInstanceKey(usage));
    const draft = drafts.get(key);
    const value =
      draft !== undefined ? draft : decodeAnswer(answers.get(key)?.value ?? null, usage.fieldType);
    const figure = typeof value === 'string' && value.trim() !== '' ? Number(value) : 0;
    if (Number.isFinite(figure)) total += figure;
  }
  return TOTAL_FORMAT.format(total);
}

const TOTAL_FORMAT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

/**
 * A repeat group: one card per active row, each rendering the group's member
 * usages addressed by that row's `instanceId`.
 *
 * Only `unbounded` rows are the respondent's to add. `count_driven` rows follow
 * a driving answer and `data_driven` rows follow a fetched fact — the server
 * refuses an add on either outright, so the button is not offered. While an add is
 * out, "Add" is disabled and turning: pressed twice, it made two rows.
 */
function RepeatGroupView(props: INodeProps) {
  const { node, instanceId, state, render, findings, readOnly, onAddRow, onRemoveRow } = props;
  const { usage } = node;
  const repeat = usage.params?.repeat;
  const rows = activeRows(state.repeats, usage.code);
  const [adding, setAdding] = React.useState(false);
  const add = () => {
    setAdding(true);
    void Promise.resolve(onAddRow(usage.code)).finally(() => setAdding(false));
  };

  const userCanAdd = repeat?.mode === 'unbounded' && repeat?.user_can_add !== false;
  const userCanRemove = repeat?.user_can_remove !== false && repeat?.mode !== 'count_driven';
  const atMax = repeat?.max != null && rows.length >= repeat.max;

  // A row-count rule ("name at least three") is addressed to the group, not to
  // any field inside it, so it belongs on the group's own header.
  const ownFindings = findings.get(addressKey(usage.code, instanceId, '')) ?? [];

  return (
    <div
      className={groupClasses(ownFindings)}
      // A repeat group is a rendered usage like any other, so the rule that an
      // element rendering a field must carry `data-address` applies to it too — it was
      // simply missed because `UsageNodeView` returns here before reaching the branch
      // that stamps a container's own address. Without this, a render sweep that walks
      // every visible usage and skips whatever has no element at its address would
      // silently skip every `repeat_group` in the survey while reporting itself complete.
      // A literal `''`, for the reason the percent-group stamp above states at length: a
      // container holds no answer of its own, so the write constraint that pins a scalar
      // `per_option_sub` to its trigger code has nothing to bite on. `answerInstanceKey`
      // returns `''` here anyway (a `repeat_group` is in `CONTAINER_FIELD_TYPES`), so this
      // is the same string either way — written as the literal so the file states one rule
      // about containers rather than two contradictory ones.
      data-address={domAddress(usage.code, instanceId, '')}
    >
      <div>
        <QuestionLabel
          as="h4"
          className="text-sm font-medium text-[var(--color-royal-blue)]"
          text={usage.label}
          required={isUsageRequired(usage.code, render, instanceId)}
        />
        {usage.helperText && (
          <p className="mt-1 text-sm text-muted-foreground">{usage.helperText}</p>
        )}
        <GroupFindings
          findings={ownFindings}
          usageOf={(code) => usageInSection(props.view, code)}
          className="mt-2"
        />
      </div>

      {rows.length === 0 && (
        <p className="text-sm text-muted-foreground">Nothing here yet.</p>
      )}

      {rows.map((row) => (
        <RepeatRowCard
          key={row.instanceId}
          {...props}
          row={row}
          canRemove={userCanRemove && !readOnly}
          onRemove={() => onRemoveRow(row.instanceId)}
        />
      ))}

      {userCanAdd && !readOnly && (
        <div>
          <Button type="button" variant="outline" size="sm" disabled={atMax || adding} onClick={add}>
            {adding ? <LoaderCircle className="animate-spin" /> : <Plus />}
            Add
          </Button>
          {atMax && (
            <span className="ml-3 text-xs text-muted-foreground">
              Maximum of {repeat?.max} reached.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function RepeatRowCard({
  row,
  canRemove,
  onRemove,
  ...props
}: INodeProps & { row: IRepeatRow; canRemove: boolean; onRemove: () => void }) {
  const { node, render } = props;

  return (
    <Card className="bg-[var(--color-off-white)] shadow-none">
      <CardContent className="grid gap-4 pt-6">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {repeatRowHead(node.usage.code, row)}
          </span>
          {canRemove && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 />
              Remove
            </Button>
          )}
        </div>

        {/* A row of two members draws them side by side from `sm` (the survey review's FA18,
            FSP13, T26, R30: a PI organisation and the involvement in it). */}
        <div
          className={cn('grid gap-4', node.members.length === 2 && 'items-start sm:grid-cols-2')}
        >
          {node.members
            // Scoped to this row: a member gated on a sibling's answer is visible in the
            // rows that opened it, not in the group. The flat map is an OR over rows and
            // would show it in all of them — see `isUsageVisible` (`snapshot-view.ts`).
            .filter((member) => isUsageVisible(member.usage.code, render, row.instanceId))
            .map((member) => (
              <div key={member.usage.code} className="grid min-w-0 gap-4">
                <FieldFor
                  {...props}
                  usage={member.usage}
                  instanceId={row.instanceId}
                  optionFollowUps={optionFollowUps(props, member, row.instanceId, (child, oneBox) => (
                    <FieldFor
                      key={child.usage.code}
                      {...props}
                      usage={child.usage}
                      instanceId={row.instanceId}
                      beside={oneBox}
                    />
                  ))}
                />
                <ChildFieldsInRow {...props} node={member} instanceId={row.instanceId} />
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * A repeat member's own children, one level deep.
 *
 * Not `ChildFields`, and the difference is the recursion: that one renders each child
 * through `UsageNodeView`, which descends again and handles a container child by its own
 * branch, while this one goes straight to `FieldFor`. So a container or a grandchild inside
 * a repeat row is **not** rendered correctly here — `ResponseField`'s switch has no
 * `percent_group`/`repeat_group` case, so such a child would fall through to `default:` and
 * get a bare text input at an address no answer lives at.
 *
 * That is a gap, not a decision, and it is left standing because no seeded survey carries
 * the shape: RIA has no container under a repeat and no repeat member with children of its
 * own. It is recorded in `docs/superpowers/specs/2026-08-20-e2e-ui-testing-design.md` §8
 * with the other two shapes the suite cannot reach, so the survey that first seeds one has
 * somewhere to start rather than a silent text box. Sharing the recursion with `ChildFields`
 * is the fix; doing it blind, with nothing able to test it, is how the wrong recursion ships.
 */
function ChildFieldsInRow(props: INodeProps) {
  const { node, instanceId } = props;
  const visible = afterList(node, visibleChildren(props, node, instanceId));

  if (visible.length === 0) return null;

  return (
    <div className="grid gap-4 border-l-2 border-[var(--color-slight-blue)] pl-4">
      {visible.map((child) => (
        <FieldFor key={child.usage.code} {...props} usage={child.usage} instanceId={instanceId} />
      ))}
    </div>
  );
}

/**
 * Children the engine has not hidden **and** whose per-option trigger is
 * selected — see `isChildTriggered` for why that second test is here and not
 * upstream. The parent's selection is read through the same overlay-first rule
 * the controls use, so a box appears on the click that selects its option
 * rather than after the batch lands.
 *
 * The parent is read at **its own** answer address, not at `''`. A parent that is itself
 * a `per_option_sub` stores its selection at its trigger code, so reading `''` there
 * found nothing and every grandchild stayed hidden for good — `FA-SV-002/criterion/other`
 * is a shipped case, and the engine had the mirror-image bug until SR-114 gave both
 * halves one accessor.
 */
function visibleChildren(props: ISectionProps, node: IUsageNode, instanceId: Nullable<string>) {
  const { render, drafts, answers } = props;
  const parentKey = addressKey(node.usage.code, instanceId, answerInstanceKey(node.usage));
  const draft = drafts.get(parentKey);
  const selection = Array.isArray(draft)
    ? draft
    : selectedCodes(answers.get(parentKey));

  // `instanceId` is null outside a repeat row and the row's own id inside one, which is
  // what a child of a repeat member needs: it is under the repeat too, so the engine
  // reports it per row.
  return node.children.filter(
    (child) =>
      isUsageVisible(child.usage.code, render, instanceId) &&
      isChildTriggered(child.usage, selection)
  );
}

/** The lists that draw a follow-up under its option; a dropdown's list closes, so not there. */
const OPTION_LISTS: ReadonlySet<string> = new Set(['radio', 'checkbox', 'multi_select']);

/**
 * A follow-up sits under the option that opened it (the survey review's G4, the plan's D15): a
 * `per_option_sub` of a radio, checkbox or multi-select list. A `sub_question` stays after the
 * list. Only where it is drawn moves; its address is its own as ever.
 */
function underOption(parent: ISnapshotUsage, child: ISnapshotUsage): boolean {
  return (
    OPTION_LISTS.has(parent.fieldType) &&
    child.childRole === 'per_option_sub' &&
    !!child.triggerOptionCode
  );
}

/** The children drawn after the list: those not drawn under an option. */
function afterList(node: IUsageNode, children: IUsageNode[]): IUsageNode[] {
  return children.filter((child) => !underOption(node.usage, child.usage));
}

/** The field types drawn as one box a follow-up can hold on its option's line. */
const ONE_BOX: ReadonlySet<string> = new Set(['int', 'number', 'percent', 'currency', 'text_short']);

/**
 * Whether an option's follow-ups are one box, drawn on the option's line (the survey review's
 * T8, T18, T20, R21; the owner's Q9 a): a lone count, percent, amount or short text with nothing
 * under it. A school's suggest box, two follow-ups or a list stay under the option's words.
 */
function isOneBox(children: IUsageNode[]): boolean {
  if (children.length !== 1) return false;
  const [{ usage, children: under }] = children;
  return ONE_BOX.has(usage.fieldType) && under.length === 0 && !usage.params?.suggest;
}

/** A list's visible follow-ups by the option they sit at, each drawn by `draw`. */
function optionFollowUps(
  props: ISectionProps,
  node: IUsageNode,
  instanceId: Nullable<string>,
  draw: (child: IUsageNode, oneBox: boolean) => React.ReactNode
): IOptionFollowUps {
  const children = new Map<string, IUsageNode[]>();
  for (const child of visibleChildren(props, node, instanceId)) {
    if (!underOption(node.usage, child.usage)) continue;
    const option = child.usage.triggerOptionCode!;
    children.set(option, [...(children.get(option) ?? []), child]);
  }
  const byOption = new Map<string, React.ReactNode[]>();
  const beside = new Set<string>();
  for (const [option, opened] of children) {
    const oneBox = isOneBox(opened);
    if (oneBox) beside.add(option);
    byOption.set(option, opened.map((child) => draw(child, oneBox)));
  }
  return { byOption, beside };
}

/**
 * Resolves one usage's address into a control: the draft overlay wins over the
 * server's answer, which is what keeps typing responsive while a batch is in flight.
 */
function FieldFor(
  props: ISectionProps & {
    usage: ISnapshotUsage;
    instanceId: Nullable<string>;
    /** What `ResponseField` draws at each option — `optionFollowUps`. */
    optionFollowUps?: IOptionFollowUps;
    /** Drawn as a percent group's member — `ResponseField.compact`. */
    compact?: boolean;
    /** Drawn on its option's line — `ResponseField.beside`. */
    beside?: boolean;
  }
) {
  const {
    usage,
    instanceId,
    state,
    render,
    yearLabels,
    drafts,
    malformed,
    answers,
    findings,
    clearedPrefill,
    readOnly,
    onChange,
    onCommit,
    onConfirm
  } = props;
  // A prefill lock is not read-only-ness: the field stays a field whose proposal is confirmed.
  const locked = readOnly || usage.isComputed;

  if (isCellsField(usage.fieldType)) {
    const rows =
      usage.fieldType === 'grid' ? gridRows(usage) : multiYearRows(usage, yearLabels);

    const layout = tableLayout(usage);
    const columns = usage.params?.grid?.columns ?? usage.params?.multi_year?.columns ?? [];
    const values = new Map<string, Record<string, string>>();
    const rowFindings = new Map<string, IFinding[]>();
    const malformedRows = new Set<string>();
    // A table's lock is per row (`prefillLock`): `locked` takes the whole table, a
    // `locked_if_filled` proposal the cells it holds.
    const tableLocked = usage.prefill?.editability === 'locked';
    const lockedRowCells = new Map<string, ILockedCells>();
    // A cells answer is one proposal per row (a figures row, SR-416): said on the row (B5-6).
    const rowPrefill = new Map<string, IRowPrefill>();
    for (const row of rows) {
      const key = addressKey(usage.code, instanceId, row.key);
      const answer = answers.get(key);
      if (!tableLocked && prefillLock(usage, answer) === 'cells' && answer?.provenance) {
        lockedRowCells.set(row.key, {
          columns: lockedCells(answer.provenance.sourceValue?.v),
          sourceLabel: sourceCopy(answer.provenance.source).proposed
        });
      }
      const draft = drafts.get(key);
      const view = prefillView(answer, usage);
      if (view) {
        rowPrefill.set(row.key, {
          view,
          proposedText:
            view.state === 'overridden'
              ? proposedCellsText(answer?.provenance?.sourceValue, columns)
              : null,
          hasUnsavedEdit: draft !== undefined
        });
      }
      const stored = decodeAnswer(answer?.value ?? null, usage.fieldType) as Record<
        string,
        string
      >;
      // A draft is the row as it stood when typed into, computed cells included; those are the
      // engine's, so they are read from the last state document — a write elsewhere (the year
      // before, a subtotal's term) moves them under a draft that is still held.
      let cells = stored;
      if (draft !== undefined) {
        cells = { ...(draft as Record<string, string>) };
        for (const column of columns) {
          if (layout.kind(row.key, column.code) !== 'computed') continue;
          if (column.code in stored) cells[column.code] = stored[column.code];
          else delete cells[column.code];
        }
      }
      values.set(row.key, cells);

      const found = findings.get(key);
      if (found?.length) rowFindings.set(row.key, found);
      // A cells answer is addressed per row, so unparseable text is a property of the
      // row, not of the table.
      if (malformed.has(key)) malformedRows.add(row.key);
    }

    // A cells answer is one row per instance key, so the findings shown here are the ones
    // addressed at the table itself (the scalar '' key) — which is what
    // `answerInstanceKey` answers for a table, `isCellsField` being one of its two
    // exclusions.
    const tableKey = addressKey(usage.code, instanceId, answerInstanceKey(usage));

    return (
      <ResponseField
        usage={usage}
        instanceId={instanceId}
        render={render}
        value=""
        findings={findings.get(tableKey) ?? []}
        required={isUsageRequired(usage.code, render, instanceId)}
        readOnly={locked}
        prefillLocked={tableLocked}
        onChange={() => undefined}
        onCommit={onCommit}
        cellRows={rows}
        cellValues={values}
        cellFindings={rowFindings}
        malformedRows={malformedRows}
        lockedRowCells={lockedRowCells}
        rowPrefill={rowPrefill}
        onRowConfirm={(rowKey) =>
          onConfirm({ usage: usage.code, instanceId, instanceKey: rowKey }, 'confirm')
        }
        onCellChange={(rowKey, cells) =>
          onChange({ usage: usage.code, instanceId, instanceKey: rowKey }, cells, usage)
        }
      />
    );
  }

  // Until `answerInstanceKey` existed, every one of these was sent (and looked up) at
  // instanceKey '', which survey-api refuses outright for a per-option-sub
  // (`writes-rejected`/`trigger_key`) — not silently: `useResponse` sets `runner.error`
  // and `rejections`, and `response-form-page.tsx` renders both as a permanent
  // "These answers were refused" banner naming the usage, alongside a stuck "Unsaved
  // changes". Still a real bug, just a loud, jargon-laden one rather than a silent one.
  const instanceKey = answerInstanceKey(usage);
  const key = addressKey(usage.code, instanceId, instanceKey);
  const answer = answers.get(key);

  const draft = drafts.get(key);
  const value = draft !== undefined ? draft : decodeAnswer(answer?.value ?? null, usage.fieldType);

  // One address, because survey-api's two finding producers now agree on it. This read used
  // to merge in a second lookup at instanceKey `''`: `Evaluator.cs`'s `validate` case passed
  // a hardcoded blank key regardless of `childRole`, so a `validate` rule aimed at a triggered
  // sub — `RIA-PM-010/last_year` on v1, July's `RIA-S1-016/last_year` before it — landed at
  // `''` while its field sat at `YES`, and reading only the control's address dropped those on the
  // floor. survey-api **SR-114** resolves a validate target through
  // `EvaluationContext.AnswerKeyOf`, the same accessor `SyntheticChecks` already used, so the
  // compensation is gone rather than merely unused: every producer of a usage-addressed
  // finding now writes the usage's own answer key, and the only one still hardcoding `''` —
  // `CheckPercentGroup` — targets a container, whose own key is `''` anyway.
  // On survey content v1 `e2e/specs/ria/pm-metrics.spec.ts`' mergers case holds this to it:
  // `RIA-PM-010.last_year`'s finding drawn on `RIA-PM-010/last_year`, a sub on `YES`.
  const ownFindings = findings.get(key) ?? [];

  return (
    <ResponseField
      usage={usage}
      instanceId={instanceId}
      render={render}
      value={value}
      findings={ownFindings}
      malformed={malformed.has(key)}
      required={isUsageRequired(usage.code, render, instanceId)}
      readOnly={locked}
      // The Teams lead's row holds its CRD and radio while they are the lead's (`answer.locked`).
      prefillLocked={prefillLock(usage, answer) === 'all' || answer?.locked === true}
      confirmOnly={isConfirmOnly(usage, answer, answers)}
      prefill={prefillView(answer, usage)}
      hasUnsavedEdit={draft !== undefined}
      cleared={clearedPrefill.has(key)}
      onChange={(next) => onChange({ usage: usage.code, instanceId, instanceKey }, next, usage)}
      onCommit={onCommit}
      onConfirm={(action) => onConfirm({ usage: usage.code, instanceId, instanceKey }, action)}
      labelSuffix={memberNamed(props, usage.code, instanceId)}
      aside={lookupAside(props, usage.code, instanceId, typeof value === 'string' ? value : '')}
      optionFollowUps={props.optionFollowUps}
      compact={props.compact}
      beside={props.beside}
    />
  );
}

/**
 * Under a CRD a lookup serves (`lookup-shapes.ts`): what this session's lookup said, and — on a
 * member row whose names a registry proposed or the respondent confirmed — "No, not this person",
 * which undoes the lookup (the survey review's G10): offered over a lock too, survey-api's unlink
 * dropping locked names as well, but never while a name is being typed, nor on the Teams lead's
 * locked row (`lead_row`). Null for any other usage.
 */
function lookupAside(
  props: ISectionProps,
  usageCode: string,
  instanceId: Nullable<string>,
  crd: string
): React.ReactNode {
  const member = instanceId ? MEMBER_LOOKUPS.find((shape) => shape.crd === usageCode) : undefined;
  const respondent =
    instanceId === null && RESPONDENT_LOOKUPS.some((shape) => shape.crd === usageCode);
  if (!member && !respondent) return null;

  const note = props.lookups?.get(member && instanceId ? instanceId : RESPONDENT_LOOKUP) ?? null;
  let notThisPerson: (() => void) | undefined;
  const leadLocked = props.answers.get(addressKey(usageCode, instanceId, ''))?.locked === true;
  if (member && instanceId && props.onUnlink && !props.readOnly && !leadLocked) {
    const names = [member.firstName, member.lastName].map((code) => ({
      usage: code,
      instanceId,
      instanceKey: ''
    }));
    const keys = names.map((name) => addressKey(name.usage, instanceId, ''));
    const looked = keys.some((key) => {
      const provenance = props.answers.get(key)?.provenance;
      return (
        (provenance?.state === 'proposed' || provenance?.state === 'confirmed') &&
        PERSON_REGISTRIES.has(provenance.source)
      );
    });
    const onUnlink = props.onUnlink;
    if (looked && !keys.some((key) => props.drafts.has(key))) {
      notThisPerson = () => onUnlink(instanceId, names);
    }
  }
  return <LookupAside note={note} crd={crd} onNotThisPerson={notThisPerson} />;
}

/**
 * "— Jane Doe" after a member's U4 follow-up, from the row's names as answered (ML §8: the label
 * naming the member is this app's), the typing included; nothing until both are set.
 */
function memberNamed(
  props: ISectionProps,
  usageCode: string,
  instanceId: Nullable<string>
): string | undefined {
  if (!instanceId) return undefined;
  const shape = MEMBER_LOOKUPS.find((candidate) => candidate.u4 === usageCode);
  if (!shape) return undefined;
  const [first, last] = [shape.firstName, shape.lastName].map((code) => {
    const key = addressKey(code, instanceId, '');
    const draft = props.drafts.get(key);
    const value =
      draft !== undefined ? draft : decodeAnswer(props.answers.get(key)?.value ?? null, 'text_short');
    return typeof value === 'string' ? value.trim() : '';
  });
  return first && last ? `— ${first} ${last}` : undefined;
}

/** A usage of the section by code, at any depth — the names a CRD's row holds. */
function usageInSection(view: ISectionView, code: string): ISnapshotUsage | undefined {
  const walk = (nodes: IUsageNode[]): ISnapshotUsage | undefined => {
    for (const node of nodes) {
      if (node.usage.code === code) return node.usage;
      const found = walk(node.children) ?? walk(node.members);
      if (found) return found;
    }
    return undefined;
  };
  return walk(view.roots);
}
